import React, { useEffect, useRef, useState } from "react";
import { makeStyles, useTheme } from "@seahorse/ui";
import FastForexPriceChartTileComponent from "../Components/FastForexPriceChartTile";
import CoTraderInsightsComponent from "../Components/CoTraderInsightsComponent"; // Importing the CoTraderInsightsComponent component
import PopOverMenu from "../../PopOverMenu";
import {
  clampChartHeight,
  getPreferredChartHeight,
  savePreferredChartHeight,
  setSessionVariable,
} from "../Utils/CoTraderUtils";

interface Context {
  id?: {
    [key: string]: string;
  };
  name?: string;
  type: string;
  [s: string]: any;
}
interface ContextProp {
  context: Context;
  currencyList: string[]; // Ensure that the type of the prop is defined
}
const CoTraderTile1: React.FunctionComponent<ContextProp> = ({
  context,
  currencyList,
}) => {
  const theme = useTheme();
  const [currencyPair, setCurrencyPair] = useState("USDJPY");
  const [useContext, setUseContext] = useState(true);
  const [updateSpeed, setUpdateSpeed] = useState(3);
  const [userLanguage, setUserLanguage] = useState(localStorage.getItem("user_language") || "English");
  const [chartHeight, setChartHeight] = useState(getPreferredChartHeight);

  const useStyles = makeStyles({
    container: {
      display: "flex",
      flexDirection: "column", // Stack the components vertically
      height: "100%",
      width: "100%",
      padding: "10px",
      backgroundColor: theme.globallink.colors.DarkGrey[500], // Background color for the combo tile
    },
    chartContainer: {
      minHeight: "150px",
      maxHeight: "500px",
      flexShrink: 0,
      overflow: "auto",
      "@media (max-height: 480px)": {
        display: "none", // Hide chart on small screens
      },
    },
    chartResizeGrip: {
      width: "44px",
      height: "12px",
      margin: "8px auto 0",
      flexShrink: 0,
      position: "relative",
      cursor: "row-resize",
      touchAction: "none",
      "&::after": {
        content: '\"\"',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "3px",
        borderRadius: "999px",
        background: "rgba(224,224,224,0.5)",
      },
      "&:hover::after": {
        background: "rgba(224,224,224,0.9)",
      },
    },
    insightsContainer: {
      flex: 1, // Take up remaining space
      overflow: "auto",
    },
    title: {
      color: "#E0E0E0",
      fontSize: "16px",
      fontWeight: "bolder",
      display: "flex",
      justifyContent: "space-between",
      padding: "4px",
      alignItems: "center", // Align items vertically in the center
    },
    popoverContainer: {
      display: "flex",
      justifyContent: "end",
      width: "100%",
    },
  });

  const classes = useStyles();
  const chartResizeStart = useRef<{
    pointerId: number;
    startY: number;
    startHeight: number;
  } | null>(null);
  const handleChartResize = (height: number) =>
    setChartHeight(savePreferredChartHeight(height));
  const getDraggedChartHeight = (
    event: React.PointerEvent<HTMLDivElement>
  ) => {
    const resizeStart = chartResizeStart.current;
    return resizeStart?.pointerId === event.pointerId
      ? clampChartHeight(
          resizeStart.startHeight + event.clientY - resizeStart.startY
        )
      : null;
  };
  const chartResizeGripProps = {
    title: "Drag to resize the chart",
    onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) return;
      event.preventDefault();
      chartResizeStart.current = {
        pointerId: event.pointerId,
        startY: event.clientY,
        startHeight: chartHeight,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    onPointerMove: (event: React.PointerEvent<HTMLDivElement>) => {
      const height = getDraggedChartHeight(event);
      if (height !== null) setChartHeight(height);
    },
    onPointerUp: (event: React.PointerEvent<HTMLDivElement>) => {
      const height = getDraggedChartHeight(event);
      chartResizeStart.current = null;
      if (height !== null) handleChartResize(height);
    },
    onPointerCancel: () => {
      chartResizeStart.current = null;
    },
  };

  useEffect(() => {
    // Function to fetch and update chart data
    if (useContext) {
      try {
        if (context) {
          const currencyPairReceived = context.instruments[0].id.ticker;
          if (currencyList.includes(currencyPairReceived)) {
            setCurrencyPair(currencyPairReceived);
          }
          console.log("context Changed , Currency Pair Changed", currencyPair);
        } else {
          console.log("No Context Available");
        }
      } catch {
        console.log("Some Error Happened");
      }
    }
  }, [context]);

  useEffect(() => {
    setSessionVariable(currencyPair);
  }, [currencyPair]);

  return (
    <div className={classes.container}>
      
      <div className={classes.title}>
        <div>{currencyPair}</div>
        <div className="d-flex flex-row ">
          <div>
            <PopOverMenu
              currencyPair={currencyPair}
              setCurrencyPair={setCurrencyPair}
              setUpdateSpeed={setUpdateSpeed}
              setUseContext={setUseContext}
              currencyList={currencyList}
              showUpdateSpeed={true}
              tenorList={null}
              setTenor={null}
              setUserLanguage={setUserLanguage}
              userLanguage={userLanguage}

            />
          </div>
        </div>
        {/* Display the currency pair */}
      </div>
      <div
        className={classes.chartContainer}
        style={{ height: `${chartHeight}px` }}
      >
        <FastForexPriceChartTileComponent
          currencyPair={currencyPair}
          chartHeight={chartHeight}
          historyPointCount={60}
          enableLiveUpdates
        />
        {/* Right column */}
      </div>
      <div className={classes.chartResizeGrip} {...chartResizeGripProps} />
      <div className={classes.insightsContainer}>
        <CoTraderInsightsComponent
          currencyPair={currencyPair}
          updateSpeed={updateSpeed}
          forward_tile={false}
          variant="preTrade"
        />{" "}
        {/* Left column */}
      </div>
    </div>
  );
};

export default CoTraderTile1;
